import Link from "next/link";
import { getModuleIconPath as moduleIconPathFromKey } from "@/lib/moduleIcons";
import { useRouter } from "next/router";
import { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { colorFromString } from "@/lib/format";
import { getSupabase } from "@/lib/supabase";
import type { Profile, Empresa, Modulo, MeResponse, UserRole, EmpresaResumida } from "@/lib/types";
import NotificationBell from "@/components/NotificationBell";
import { useTheme } from "@/components/ThemeProvider";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import type { ThemeMode, LocaleCode } from "@/lib/preferences";
import { OnboardingTour } from "@/components/OnboardingTour";  // R24_TOUR_WIRED
import { Footer } from "@/components/Footer";  // R23_FOOTER_WIRED

// ═════════════════════════════════════════════════════════════════════════════
// Types internos
// ═════════════════════════════════════════════════════════════════════════════

type MenuItem  = { labelKey: string; href: string; icon: string };
type NavModule = { slug: string; labelKey: string; icon: string; href?:  string;
  items?: MenuItem[] };

// Versão da aplicação — exibida no rodapé da sidebar.
// R19: lê de package.json via next.config.js injetar NEXT_PUBLIC_APP_VERSION.
// Fallback "dev" pra build local sem env var setada.
const APP_VERSION = `v${process.env.NEXT_PUBLIC_APP_VERSION ?? "dev"}`;

// ═════════════════════════════════════════════════════════════════════════════
// MeContext — fonte única do user/empresa/modulos pra toda a app autenticada
// ═════════════════════════════════════════════════════════════════════════════

type MeContextValue = { me: MeResponse; refreshMe: () => Promise<void> };
const MeContext = createContext<MeContextValue | null>(null);
export function useMe(): MeResponse {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error("useMe deve ser usado dentro de <Layout>");
  return ctx.me;
}

export function useMeRefresh(): () => Promise<void> {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error("useMeRefresh deve ser usado dentro de <Layout>");
  return ctx.refreshMe;
}

// ═════════════════════════════════════════════════════════════════════════════
// Sidebar module catalog
// `agents` e `admin` são atalhos da topbar — não entram aqui.
// Sub-itens são as páginas internas de cada módulo. Quando o módulo ainda
// não foi implementado (Fase 3+), o item leva pra placeholder "Em construção".
// ═════════════════════════════════════════════════════════════════════════════

const DASHBOARD_HREF = "/app";
const DASHBOARD_ICON =
  "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6";

const COMUNICADOS_HREF = "/comunicados";
const COMUNICADOS_ICON =
  "M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z";


function moduleIconPath(icone: string | null | undefined, fallbackPath: string): string {
  if (!icone) return fallbackPath;
  const path = moduleIconPathFromKey(icone);
  return path || fallbackPath;
}

const SIDEBAR_MODULES: NavModule[] = [
  {
    slug:  "conciliacao",
    labelKey: "modules.conciliacao.title",
    icon:  "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
    items: [
      { labelKey: "modules.conciliacao.dashboard",   href: "/conciliacao/dashboard",   icon: "M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25a2.25 2.25 0 01-2.25-2.25v-2.25z" },
      { labelKey: "modules.conciliacao.consulta",    href: "/conciliacao/consulta",    icon: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" },
      { labelKey: "modules.conciliacao.importacao",  href: "/conciliacao/importacao",  icon: "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" },
      { labelKey: "modules.conciliacao.config",       href: "/conciliacao/config-conta", icon: "M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" },
    ],
  },
  {
    slug:  "task-scheduler",
    labelKey: "modules.taskScheduler.title",
    icon:  "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
    items: [
      { labelKey: "modules.taskScheduler.tasks",       href: "/task-scheduler",             icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" },
      { labelKey: "modules.taskScheduler.runs",        href: "/task-scheduler/runs",        icon: "M3 12a9 9 0 1018 0 9 9 0 00-18 0zm9-4.5v4.5l3 1.5" },
    ],
  },
  {
    slug:  "conexoes",
    labelKey: "modules.conexoes.title",
    icon:  "M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244",
    href:  "/conexoes",
  },
  {
    slug:  "osigu",
    labelKey: "modules.osigu.title",
    icon:  "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    items: [
      { labelKey: "modules.osigu.pagos",   href: "/osigu/pagos",   icon: "M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" },
      { labelKey: "modules.osigu.cobranca", href: "/osigu/cobranca", icon: "M3 21h18M5 21V8l7-5 7 5v13M9 21v-6h6v6M9 13h.01M15 13h.01" },
      { labelKey: "modules.osigu.orizon",   href: "/osigu/orizon",   icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" },
    ],
  },
  {
    slug:  "cash-position",
    labelKey: "modules.cashPosition.title",
    icon:  "M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z",
    items: [
      { labelKey: "modules.cashPosition.dashboard", href: "/cash-position/dashboard", icon: "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" },
      { labelKey: "modules.cashPosition.bancos", href: "/cash-position/bancos", icon: "M3 21h18M5 21V8l7-5 7 5v13M9 21v-6h6v6M9 13h.01M15 13h.01" },
      { labelKey: "modules.cashPosition.lancamentos", href: "/cash-position/lancamentos", icon: "M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.007H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.007H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.007H3.75v-.007zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" },
      { labelKey: "modules.cashPosition.trm", href: "/cash-position/trm", icon: "M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" },
    ],
  },
];




// ─── Top-bar shortcuts ────────────────────────────────────────────────────────

const TOP_AGENTS = {
  slug:  "agents",
  labelKey: "topbar.agents",
  href:  "/agents",
  icon:  "M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z",
};

const TOP_ADMIN = {
  slug:  "admin",
  labelKey: "topbar.admin",
  href:  "/admin",
  icon:  "M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.249-8.25-3.286z",
  roles: ["admin", "superadmin", "platform_admin"] as UserRole[],
};

const TOP_PLATFORM = {
  slug:  "platform",
  labelKey: "topbar.platform",
  href:  "/platform",
  icon:  "M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A9.004 9.004 0 0118 12c0 .654-.072 1.288-.204 1.918M3.157 7.582a9.004 9.004 0 000 7.336M14 12a2 2 0 11-4 0 2 2 0 014 0z",
  roles: ["platform_admin"] as UserRole[],
};

const ROLE_BADGE: Record<string, { labelKey: string; color: string }> = {
  platform_admin: { labelKey: "roles.platform_admin", color: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"       },
  superadmin:     { labelKey: "roles.superadmin",     color: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" },
  admin:          { labelKey: "roles.admin",          color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"       },
  user:           { labelKey: "roles.user",           color: "bg-app-soft text-app-2"                                                  },
};

// ═════════════════════════════════════════════════════════════════════════════
// MillerMark + Brand
// ═════════════════════════════════════════════════════════════════════════════

function MillerMark({ className = "h-8 w-auto" }: { className?: string }) {
  return (
    <img
      src="/logo-miller.png"
      alt="Miller"
      className={className}
      draggable={false}
    />
  );
}

function Brand({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <Link href={DASHBOARD_HREF} className="flex items-center gap-3 group select-none">
      <MillerMark className="h-8 w-auto shrink-0 transition-transform duration-200 group-hover:scale-95" />
      {!collapsed && (
        <span
          className="text-app-2 text-[11px] font-semibold uppercase whitespace-nowrap"
          style={{ letterSpacing: "0.34em" }}
        >
          Platform
        </span>
      )}
    </Link>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// NavIcon
// ═════════════════════════════════════════════════════════════════════════════

function NavIcon({ d, className = "w-[18px] h-[18px]" }: { d: string; className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SidebarItem
// ═════════════════════════════════════════════════════════════════════════════

function SidebarItem({
  href, label, icon, active, collapsed, indent = false, onNavigate,
}: {
  href: string; label: string; icon: string;
  active: boolean; collapsed: boolean;
  indent?: boolean; onNavigate?: () => void;
}) {
  return (
    <Link
      href={href}
      title={collapsed ? label : undefined}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={`
        relative flex items-center rounded-xl text-sm font-medium
        transition-colors duration-150 group
        ${collapsed ? "justify-center px-2 py-2.5" : `gap-3 ${indent ? "pl-7 pr-3" : "px-3"} py-2`}
        ${active
          ? "bg-app-hover text-app-1"
          : "text-app-3 hover:bg-app-hover hover:text-app-1"}
      `}
    >
      {active && (
        <span
          aria-hidden
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-accent"
          style={{ boxShadow: "0 0 0 1px rgba(var(--accent-rgb) / 0.18)" }}
        />
      )}
      <NavIcon
        d={icon}
        className={`shrink-0 ${collapsed ? "w-[20px] h-[20px]" : "w-[17px] h-[17px]"} ${active ? "text-app-1" : "text-app-3 group-hover:text-app-2"}`}
      />
      {!collapsed && (
        <span className={`truncate ${active ? "font-semibold" : ""}`}>{label}</span>
      )}
    </Link>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SidebarGroup — collapsible module group
// ═════════════════════════════════════════════════════════════════════════════

function SidebarGroup({
  module: mod, currentPath, collapsed, defaultOpen, onNavigate,
}: {
  module: NavModule; currentPath: string; collapsed: boolean;
  defaultOpen: boolean; onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { if (defaultOpen) setOpen(true); }, [defaultOpen]);

  // R18: módulo dinâmico (sem items) renderiza como link direto pra /{slug} ou mod.href
  const items = mod.items ?? [];
  const hasSubItems = items.length > 0;

  if (!hasSubItems) {
    const directHref = mod.href ?? `/${mod.slug}`;
    const directActive = currentPath === directHref || currentPath.startsWith(directHref + "/");
    return (
      <SidebarItem
        href={directHref}
        label={mod.labelKey?.startsWith("__direct:") ? mod.labelKey.slice(9) : (mod.href ? mod.labelKey : t(mod.labelKey))}
        icon={mod.icon}
        active={directActive}
        collapsed={collapsed}
        onNavigate={onNavigate}
      />
    );
  }

  const groupActive = items.some(
    i => currentPath === i.href || currentPath.startsWith(i.href + "/")
  );

  if (collapsed) {
    const first = items[0];
    return (
      <SidebarItem
        href={first.href}
        label={mod.labelKey?.startsWith("__direct:") ? mod.labelKey.slice(9) : (mod.href ? mod.labelKey : t(mod.labelKey))}
        icon={mod.icon}
        active={groupActive}
        collapsed
        onNavigate={onNavigate}
      />
    );
  }

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className={`
          w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium
          transition-colors duration-150 group
          ${groupActive && !open
            ? "text-app-1"
            : "text-app-2 hover:bg-app-hover hover:text-app-1"}
        `}
        aria-expanded={open}
      >
        <NavIcon d={mod.icon} className={`w-[17px] h-[17px] shrink-0 ${groupActive ? "text-app-1" : "text-app-3 group-hover:text-app-2"}`} />
        <span className="flex-1 text-left truncate">{mod.labelKey?.startsWith("__direct:") ? mod.labelKey.slice(9) : (mod.href ? mod.labelKey : t(mod.labelKey))}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`w-3.5 h-3.5 text-app-3 transition-transform duration-200 ${open ? "rotate-90" : ""}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m9 5 7 7-7 7" />
        </svg>
      </button>

      <div
        className="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <div className="pt-1 pb-1 space-y-0.5">
            {(mod.items ?? []).map(item => {
              const active = currentPath === item.href || currentPath.startsWith(item.href + "/");
              return (
                <SidebarItem
                  key={item.href}
                  href={item.href}
                  label={t(item.labelKey)}
                  icon={item.icon}
                  active={active}
                  collapsed={false}
                  indent
                  onNavigate={onNavigate}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SidebarBody — shared between desktop sidebar and mobile drawer
// ═════════════════════════════════════════════════════════════════════════════

function SidebarBody({
  modules, currentPath, collapsed, onNavigate,
}: {
  modules: NavModule[]; currentPath: string; collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const activeSlug = useMemo(() => {
    return modules.find(m => (m.items ?? []).some(
      i => currentPath === i.href || currentPath.startsWith(i.href + "/")
    ))?.slug ?? null;
  }, [modules, currentPath]);

  const dashActive = currentPath === DASHBOARD_HREF || currentPath.startsWith(DASHBOARD_HREF + "/");

  return (
    <nav className="flex-1 overflow-y-auto px-2.5 pb-4">
      <div className={`pt-3 ${collapsed ? "" : "pb-1"}`}>
        <SidebarItem
          href={DASHBOARD_HREF}
          label={t("sidebar.home")}
          icon={DASHBOARD_ICON}
          active={dashActive}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />
        <SidebarItem
          href={COMUNICADOS_HREF}
          label={t("sidebar.comunicados")}
          icon={COMUNICADOS_ICON}
          active={currentPath === COMUNICADOS_HREF || currentPath.startsWith(COMUNICADOS_HREF + "/")}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />
      </div>

      {modules.length > 0 && (
        <>
          {!collapsed && (
            <div className="px-3 pt-5 pb-2 flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-app-3">{t("sidebar.modulesHeader")}</span>
              <span className="flex-1 h-px bg-app-soft" />
            </div>
          )}
          {collapsed && <div className="my-3 mx-3 h-px bg-app-soft" />}

          <div className="space-y-0.5">
            {modules.map(mod => (
              <SidebarGroup
                key={mod.slug}
                module={mod}
                currentPath={currentPath}
                collapsed={collapsed}
                defaultOpen={mod.slug === activeSlug}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </>
      )}

      {modules.length === 0 && !collapsed && (
        <div className="px-3 py-6 mt-2 rounded-xl bg-app-soft text-center">
          <p className="text-xs text-app-3 leading-relaxed">
            {t("sidebar.noModules")}<br />
            <span className="text-app-muted">{t("sidebar.contactAdmin")}</span>
          </p>
        </div>
      )}
    </nav>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// DesktopSidebar
// ═════════════════════════════════════════════════════════════════════════════

function DesktopSidebar({
  modules, currentPath, collapsed, onToggle,
}: {
  modules: NavModule[]; currentPath: string;
  collapsed: boolean; onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <aside
      data-tour="sidebar"
      className={`
        hidden lg:flex fixed inset-y-0 left-0 z-30 flex-col
        bg-app-surface border-r border-app
        transition-[width] duration-200 ease-out
      `}
      style={{
        width: collapsed ? "4.5rem" : "14rem",
        backgroundImage: "linear-gradient(180deg, var(--surface-2) 0%, var(--surface) 140px)",
      }}
    >
      <div className={`flex items-center ${collapsed ? "justify-center" : "justify-between"} px-3 h-14 border-b border-app-subtle`}>
        {collapsed ? (
          <Link href={DASHBOARD_HREF} className="flex items-center justify-center group" title={t("sidebar.tooltipBrand")}>
            <MillerMark className="h-8 w-auto transition-transform duration-200 group-hover:scale-95" />
          </Link>
        ) : (
          <Brand />
        )}
        {!collapsed && (
          <button
            onClick={onToggle}
            title={t("sidebar.collapseMenu")}
            className="btn-icon btn-icon-sm -mr-1"
            aria-label={t("sidebar.collapseMenu")}
          >
            <NavIcon d="M11 19l-7-7 7-7m8 14l-7-7 7-7" className="w-4 h-4" />
          </button>
        )}
      </div>

      {collapsed && (
        <div className="flex justify-center pt-2">
          <button
            onClick={onToggle}
            title={t("sidebar.expandMenu")}
            className="btn-icon btn-icon-sm"
            aria-label={t("sidebar.expandMenu")}
          >
            <NavIcon d="M13 5l7 7-7 7M5 5l7 7-7 7" className="w-4 h-4" />
          </button>
        </div>
      )}

      <SidebarBody modules={modules} currentPath={currentPath} collapsed={collapsed} />

      {/* Footer com versão */}
      <div className={`shrink-0 border-t border-app-subtle px-3 py-2.5 ${collapsed ? "text-center" : ""}`}>
        {collapsed ? (
          <span className="text-[9px] font-mono text-app-muted">{APP_VERSION}</span>
        ) : (
          <div className="flex items-center justify-between text-[10px] text-app-muted">
            <span className="font-mono">Miller Platform</span>
            <span className="font-mono tabular-nums">{APP_VERSION}</span>
          </div>
        )}
      </div>
    </aside>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MobileDrawer
// ═════════════════════════════════════════════════════════════════════════════

function MobileDrawer({
  open, onClose, modules, currentPath,
}: {
  open: boolean; onClose: () => void;
  modules: NavModule[]; currentPath: string;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <div
        onClick={onClose}
        className={`lg:hidden fixed inset-0 z-50 transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        style={{ background: "rgba(10,10,10,0.45)", backdropFilter: "blur(4px)" }}
      />
      <aside
        className={`
          lg:hidden fixed top-0 left-0 z-50 h-full w-[17rem] bg-app-surface
          flex flex-col transition-transform duration-300
          ${open ? "translate-x-0" : "-translate-x-full"}
        `}
        style={{ boxShadow: "var(--shadow-dropdown)" }}
      >
        <div className="flex items-center justify-between px-4 h-14 border-b border-app-subtle">
          <Brand />
          <button onClick={onClose} className="btn-icon btn-icon-sm" aria-label={t("common.close")}>
            <NavIcon d="M6 18L18 6M6 6l12 12" className="w-5 h-5" />
          </button>
        </div>
        <SidebarBody modules={modules} currentPath={currentPath} collapsed={false} onNavigate={onClose} />

        {/* Footer com versão */}
        <div className="shrink-0 border-t border-app-subtle px-4 py-2.5 flex items-center justify-between text-[10px] text-app-muted">
          <span className="font-mono">Miller Platform</span>
          <span className="font-mono tabular-nums">{APP_VERSION}</span>
        </div>
      </aside>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// UserMenu — top-bar avatar dropdown
// ═════════════════════════════════════════════════════════════════════════════

function UserMenu({ user, empresa, empresas }: { user: Profile; empresa: Empresa | null; empresas: EmpresaResumida[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [switchando, setSwitchando] = useState<string | null>(null);
  const [imgErr, setImgErr] = useState(false);
  const [empresasOpen, setEmpresasOpen] = useState(false);  // submenu de empresas (quando 5+)
  const [buscaEmpresa, setBuscaEmpresa] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const { theme, setTheme } = useTheme();

  // quando fecha o menu, recolhe o submenu de empresas e limpa a busca
  useEffect(() => { if (!open) { setEmpresasOpen(false); setBuscaEmpresa(""); } }, [open]);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  async function handleLogout() {
    try { sessionStorage.removeItem("miller.escolheu_empresa"); } catch {}
    const supabase = getSupabase();
    await supabase.auth.signOut();
    window.location.href = "/login";
  }

  async function trocarEmpresa(empresaId: string) {
    if (switchando) return;
    setSwitchando(empresaId);
    try {
      await api.post(`/me/switch-empresa/${empresaId}`);
      try { sessionStorage.setItem("miller.escolheu_empresa", "1"); } catch {}
      // Reload pra refetchar /me e atualizar contexto inteiro
      window.location.href = "/app";
    } catch (e) {
      setSwitchando(null);
    }
  }

  const badge    = ROLE_BADGE[user.role] ?? ROLE_BADGE.user;
  const displayName = (user.nome || user.apelido || user.email || "?") as string;
  const initials = displayName.trim().split(/\s+/).slice(0, 2).map(x => x[0] || "").join("").toUpperCase() || "?";
  const empresaNome = empresa?.nome ?? t("userMenu.noCompany");
  const hasMultiple = empresas.length >= 2;
  // até 4 empresas: lista inline. 5+: vira botão que expande um submenu com busca.
  const MUITAS_EMPRESAS = 4;
  const usaSubmenu = empresas.length > MUITAS_EMPRESAS;
  const empresasFiltradas = empresas.filter(e => {
    const q = buscaEmpresa.trim().toLowerCase();
    return !q || e.nome.toLowerCase().includes(q);
  });

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-app-hover transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div className="relative">
          <div className="w-8 h-8 rounded-full overflow-hidden ring-1 ring-app text-white flex items-center justify-center text-[11px] font-bold shrink-0" style={{ backgroundColor: colorFromString(displayName) }}>
            {user.foto_url && !imgErr ? (
              <img src={user.foto_url} alt={displayName} onError={() => setImgErr(true)} className="w-full h-full object-cover" />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-[var(--surface)]" />
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`hidden sm:block w-3.5 h-3.5 text-app-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
        </svg>
      </button>

      <div
        role="menu"
        className={`
          absolute right-0 top-[calc(100%+8px)] w-72 rounded-2xl bg-app-surface border border-app z-50
          transition-all duration-200 origin-top-right
          ${open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"}
        `}
        style={{ boxShadow: "var(--shadow-dropdown)" }}
      >
        <div className="px-4 py-4 flex items-center gap-3 border-b border-app-subtle">
          <div className="w-11 h-11 rounded-xl overflow-hidden text-white flex items-center justify-center text-sm font-bold shrink-0" style={{ backgroundColor: colorFromString(displayName) }}>
            {user.foto_url && !imgErr ? (
              <img src={user.foto_url} alt={displayName} onError={() => setImgErr(true)} className="w-full h-full object-cover" />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-app-1 truncate">{user.nome}</p>
            <p className="text-[11px] text-app-3 truncate mt-0.5">{empresaNome}</p>
          </div>
          <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${badge.color}`}>
            {t(badge.labelKey)}
          </span>
        </div>
        <div className="p-1.5">
          {/* Meu Perfil — logo abaixo do cabeçalho */}
          <Link
            href="/perfil"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-app-2 hover:bg-app-hover hover:text-app-1 transition-colors font-medium"
          >
            <NavIcon d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" className="w-4 h-4 text-app-3 shrink-0" />
            {t("userMenu.myProfile")}
          </Link>

          {hasMultiple && <div className="h-px bg-app-subtle my-1" />}

          {hasMultiple && !usaSubmenu && (
            <>
              <div className="px-3 pt-2 pb-1.5">
                <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-app-3">
                  {t("userMenu.switchEmpresa", "Trocar de empresa")}
                </p>
              </div>
              <div className="max-h-56 overflow-y-auto">
                {empresas.map(e => (
                  <button
                    key={e.id}
                    onClick={() => !e.is_current && trocarEmpresa(e.id)}
                    disabled={e.is_current || switchando !== null}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-colors ${
                      e.is_current
                        ? "bg-emerald-50 dark:bg-emerald-950/40 cursor-default"
                        : "hover:bg-app-hover cursor-pointer"
                    } disabled:opacity-100`}
                  >
                    {e.logo_url ? (
                      <img src={e.logo_url} alt={e.nome} className="w-7 h-7 rounded object-contain bg-app-soft border border-app shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded bg-app-soft border border-app flex items-center justify-center text-[11px] font-bold text-app-3 shrink-0">
                        {e.nome[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-app-1 truncate">{e.nome}</p>
                      <p className="text-[10px] text-app-3 uppercase tracking-wider">{e.role}</p>
                    </div>
                    {e.is_current && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900 shrink-0">
                        {t("userMenu.empresaCurrent", "Atual")}
                      </span>
                    )}
                    {switchando === e.id && (
                      <div className="w-3 h-3 border-2 border-app-strong border-t-app-1 rounded-full animate-spin shrink-0" />
                    )}
                  </button>
                ))}
              </div>
              <div className="h-px bg-app-subtle my-1" />
            </>
          )}

          {hasMultiple && usaSubmenu && (
            <>
              {/* botão que expande o submenu de empresas (quando 5+) */}
              <button
                onClick={() => setEmpresasOpen(v => !v)}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-app-2 hover:bg-app-hover hover:text-app-1 transition-colors font-medium"
              >
                {empresa?.logo_url ? (
                  <img src={empresa.logo_url} alt={empresaNome} className="w-5 h-5 rounded object-contain bg-app-soft border border-app shrink-0" />
                ) : (
                  <NavIcon d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-3" className="w-4 h-4 text-app-3 shrink-0" />
                )}
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-app-1 truncate">{t("userMenu.switchEmpresa", "Trocar de empresa")}</p>
                  <p className="text-[10px] text-app-3 truncate">{empresaNome}</p>
                </div>
                <svg className={`w-4 h-4 text-app-3 transition-transform ${empresasOpen ? "rotate-180" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
                </svg>
              </button>

              {/* submenu deslizante */}
              <div className={`overflow-hidden transition-all duration-200 ${empresasOpen ? "max-h-80 opacity-100" : "max-h-0 opacity-0"}`}>
                <div className="px-2 pt-1 pb-2">
                  <input
                    value={buscaEmpresa}
                    onChange={e => setBuscaEmpresa(e.target.value)}
                    placeholder={t("userMenu.buscarEmpresa", { defaultValue: "Buscar empresa…" })}
                    className="w-full rounded-lg border border-app bg-app-surface text-app-1 px-2.5 py-1.5 text-sm mb-1"
                  />
                  <div className="max-h-56 overflow-y-auto">
                    {empresasFiltradas.length === 0 ? (
                      <p className="px-3 py-3 text-center text-app-3 text-xs">{t("userMenu.nenhumaEmpresa", { defaultValue: "Nenhuma empresa." })}</p>
                    ) : empresasFiltradas.map(e => (
                      <button
                        key={e.id}
                        onClick={() => !e.is_current && trocarEmpresa(e.id)}
                        disabled={e.is_current || switchando !== null}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-colors ${
                          e.is_current ? "bg-emerald-50 dark:bg-emerald-950/40 cursor-default" : "hover:bg-app-hover cursor-pointer"
                        } disabled:opacity-100`}
                      >
                        {e.logo_url ? (
                          <img src={e.logo_url} alt={e.nome} className="w-7 h-7 rounded object-contain bg-app-soft border border-app shrink-0" />
                        ) : (
                          <div className="w-7 h-7 rounded bg-app-soft border border-app flex items-center justify-center text-[11px] font-bold text-app-3 shrink-0">
                            {e.nome[0]?.toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0 text-left">
                          <p className="text-sm font-medium text-app-1 truncate">{e.nome}</p>
                          <p className="text-[10px] text-app-3 uppercase tracking-wider">{e.role}</p>
                        </div>
                        {e.is_current && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900 shrink-0">
                            {t("userMenu.empresaCurrent", "Atual")}
                          </span>
                        )}
                        {switchando === e.id && (
                          <div className="w-3 h-3 border-2 border-app-strong border-t-app-1 rounded-full animate-spin shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="h-px bg-app-subtle my-1" />
            </>
          )}

          {/* Theme toggle: Light / Dark / System / Auto */}
          <div className="px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-app-3 mb-1.5">{t("userMenu.themeLabel")}</p>
            <ThemeSegmented value={theme} onChange={setTheme} />
          </div>

          {/* Language switcher */}
          <div className="px-3 pb-2">
            <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-app-3 mb-1.5">{t("appearance.languageLabel")}</p>
            <LanguageSwitcher variant="compact" />
          </div>

          <div className="h-px bg-app-subtle my-1" />
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors font-medium"
          >
            <NavIcon d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" className="w-4 h-4 shrink-0" />
            {t("userMenu.logout")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ThemeSegmented — 4 opções em grid 2x2 (Light/Dark/System/Auto) ─────────

function ThemeSegmented({ value, onChange }: { value: ThemeMode; onChange: (t: ThemeMode) => void }) {
  const { t } = useTranslation();
  const opts: { v: ThemeMode; labelKey: string; ariaKey: string; icon: string }[] = [
    { v: "light",  labelKey: "theme.light",   ariaKey: "theme.ariaThemeLight",   icon: "M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" },
    { v: "dark",   labelKey: "theme.dark",    ariaKey: "theme.ariaThemeDark",    icon: "M21.752 15.002A9.72 9.72 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" },
    { v: "system", labelKey: "theme.system",  ariaKey: "theme.ariaThemeSystem",  icon: "M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" },
    { v: "auto",   labelKey: "theme.auto",    ariaKey: "theme.ariaThemeAuto",    icon: "M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" },
  ];
  return (
    <div className="grid grid-cols-2 gap-1 p-0.5 rounded-lg bg-app-soft">
      {opts.map(o => {
        const active = value === o.v;
        const label = t(o.labelKey);
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            title={label}
            aria-label={t(o.ariaKey)}
            aria-pressed={active}
            className={`
              flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[11px] font-medium
              transition-colors duration-150
              ${active
                ? "bg-app-surface text-app-1 shadow-sm"
                : "text-app-3 hover:text-app-1"}
            `}
          >
            <NavIcon d={o.icon} className="w-3.5 h-3.5" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Date helpers
// ═════════════════════════════════════════════════════════════════════════════

function useNow() {
  const [now, setNow] = useState<Date | null>(null);
  useEffect(() => {
    setNow(new Date());
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

// Locale → BCP-47 tag pra Intl APIs
function intlLocale(loc: LocaleCode): string {
  if (loc === "pt") return "pt-BR";
  if (loc === "en") return "en-US";
  return "es-ES";
}

function formatTime(d: Date, loc: LocaleCode) {
  return d.toLocaleTimeString(intlLocale(loc), { hour: "2-digit", minute: "2-digit" });
}
function formatDate(d: Date, loc: LocaleCode) {
  // Ex PT: "qui, 08 mai"   EN: "Thu, May 08"   ES: "jue, 08 may"
  const tag = intlLocale(loc);
  const weekday = new Intl.DateTimeFormat(tag, { weekday: "short" }).format(d).replace(/\.$/, "");
  const day     = String(d.getDate()).padStart(2, "0");
  const month   = new Intl.DateTimeFormat(tag, { month: "short" }).format(d).replace(/\.$/, "");
  if (loc === "en") return `${weekday}, ${month} ${day}`;
  return `${weekday}, ${day} ${month}`;
}
function getGreetingKey(d: Date): { textKey: string; emoji: string } {
  const h = d.getHours();
  if (h < 12) return { textKey: "topbar.greetingMorning",   emoji: "☀️" };
  if (h < 18) return { textKey: "topbar.greetingAfternoon", emoji: "👋" };
  return        { textKey: "topbar.greetingEvening", emoji: "🌙" };
}

// ═════════════════════════════════════════════════════════════════════════════
// TopBar pieces
// ═════════════════════════════════════════════════════════════════════════════

// ─── WorkspaceSwitcher — chip workspace com dropdown de troca (R17) ─────────
function WorkspaceSwitcher({ empresa, empresas }: { empresa: Empresa | null; empresas: EmpresaResumida[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [switchando, setSwitchando] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  async function trocar(empresaId: string) {
    if (switchando) return;
    setSwitchando(empresaId);
    try {
      await api.post(`/me/switch-empresa/${empresaId}`);
      try { sessionStorage.setItem("miller.escolheu_empresa", "1"); } catch {}
      window.location.href = "/app";
    } catch {
      setSwitchando(null);
    }
  }

  if (!empresa) return null;

  const empresaAtualResumida = empresas.find(e => e.is_current);
  const logoAtual = empresaAtualResumida?.logo_url ?? null;
  const hasMultiple = empresas.length >= 2;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => hasMultiple && setOpen(v => !v)}
        disabled={!hasMultiple}
        className={`inline-flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-full bg-app-soft text-[13px] font-semibold text-app-1 max-w-[280px] transition-colors ${
          hasMultiple ? "hover:bg-app-hover cursor-pointer" : "cursor-default"
        }`}
        aria-haspopup={hasMultiple ? "menu" : undefined}
        aria-expanded={hasMultiple ? open : undefined}
      >
        {/* Logo da empresa ou inicial */}
        <span className="relative shrink-0">
          {logoAtual ? (
            <img src={logoAtual} alt={empresa.nome} className="w-8 h-8 rounded-lg object-contain bg-white border border-app" />
          ) : (
            <span className="w-8 h-8 rounded-lg bg-app-surface border border-app flex items-center justify-center text-xs font-bold text-app-3">
              {empresa.nome[0]?.toUpperCase()}
            </span>
          )}
        </span>
        <span className="truncate">{empresa.nome}</span>
        {hasMultiple && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`w-3 h-3 text-app-3 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
          </svg>
        )}
      </button>

      {/* Dropdown */}
      {hasMultiple && (
        <div
          role="menu"
          className={`
            absolute left-0 top-[calc(100%+8px)] w-72 rounded-2xl bg-app-surface border border-app z-50
            transition-all duration-200 origin-top-left
            ${open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"}
          `}
          style={{ boxShadow: "var(--shadow-dropdown)" }}
        >
          <div className="px-4 py-3 border-b border-app-subtle">
            <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-app-3">
              {t("workspaceSwitcher.label", "Trocar de empresa")}
            </p>
          </div>
          <div className="p-1.5 max-h-72 overflow-y-auto">
            {empresas.map(e => (
              <button
                key={e.id}
                onClick={() => !e.is_current && trocar(e.id)}
                disabled={e.is_current || switchando !== null}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-colors ${
                  e.is_current
                    ? "bg-emerald-50 dark:bg-emerald-950/40 cursor-default"
                    : "hover:bg-app-hover cursor-pointer"
                } disabled:opacity-100`}
              >
                {e.logo_url ? (
                  <img src={e.logo_url} alt={e.nome} className="w-8 h-8 rounded-lg object-contain bg-app-soft border border-app shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-app-soft border border-app flex items-center justify-center text-xs font-bold text-app-3 shrink-0">
                    {e.nome[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-app-1 truncate">{e.nome}</p>
                  <p className="text-[10px] text-app-3 uppercase tracking-wider">{e.role}</p>
                </div>
                {e.is_current && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900 shrink-0">
                    {t("workspaceSwitcher.current", "Atual")}
                  </span>
                )}
                {switchando === e.id && (
                  <div className="w-3 h-3 border-2 border-app-strong border-t-app-1 rounded-full animate-spin shrink-0" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function TopBarGreeting({ user, empresa, empresas }: { user: Profile; empresa: Empresa | null; empresas: EmpresaResumida[] }) {
  const { t } = useTranslation();
  const now = useNow();
  if (!now) return <div aria-hidden className="h-6 w-32" />;

  const { textKey, emoji } = getGreetingKey(now);
  const greetingText = t(textKey);
  const firstName = user.apelido?.trim() || user.nome.split(" ")[0];

  return (
    <div className="hidden lg:flex items-center gap-3 min-w-0">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-base leading-none" aria-hidden>{emoji}</span>
        <span className="text-[13px] text-app-3 whitespace-nowrap">{greetingText},</span>
        <span className="text-[14px] font-semibold text-app-1 truncate">{firstName}</span>
      </div>
    </div>
  );
}

function TopBarClock() {
  const { locale } = useTheme();
  const now = useNow();
  if (!now) return <div aria-hidden className="h-6 w-24" />;

  return (
    <div className="hidden md:flex items-baseline gap-2 whitespace-nowrap">
      <span className="font-mono tabular-nums text-[14px] font-semibold text-app-1 leading-none">
        {formatTime(now, locale)}
      </span>
      <span className="hidden lg:inline text-[12px] text-app-3 leading-none">
        {formatDate(now, locale)}
      </span>
    </div>
  );
}

function TopBar({
  user, empresa, empresas, isAdmin, isPlatformAdmin, hasAgents, currentPath, onMobileMenu,
}: {
  user: Profile; empresa: Empresa | null;
  empresas: EmpresaResumida[];
  isAdmin: boolean; isPlatformAdmin: boolean; hasAgents: boolean;
  currentPath: string;
  onMobileMenu: () => void;
}) {
  const { t } = useTranslation();
  const isAgentsActive   = currentPath === TOP_AGENTS.href   || currentPath.startsWith(TOP_AGENTS.href + "/");
  const isAdminActive    = currentPath === TOP_ADMIN.href    || currentPath.startsWith(TOP_ADMIN.href + "/");
  const isPlatformActive = currentPath === TOP_PLATFORM.href || currentPath.startsWith(TOP_PLATFORM.href + "/");

  return (
    <header className="sticky top-0 z-40 h-14 backdrop-blur-md bg-[var(--surface)]/85 border-b border-app">
      <div className="h-full px-4 sm:px-6 lg:pr-10 flex items-center gap-4">
        {/* ═══ HAMBURGUER MOBILE ═══ */}
        <button
          onClick={onMobileMenu}
          className="btn-icon btn-icon-sm lg:hidden shrink-0"
          aria-label={t("sidebar.openMenu")}
        >
          <NavIcon d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" className="w-5 h-5" />
        </button>
        <div className="lg:hidden shrink-0">
          <Brand />
        </div>

        {/* ═══ ESQUERDA: greeting · clock · workspace ═══ */}
        <div className="hidden lg:flex items-center gap-3 min-w-0">
          <div className="hidden xl:block min-w-0">
            <TopBarGreeting user={user} empresa={empresa} empresas={empresas} />
          </div>
          <span className="hidden 2xl:block w-px h-5 bg-app shrink-0" aria-hidden />
          <div className="hidden 2xl:block shrink-0">
            <TopBarClock />
          </div>
          <span className="hidden xl:block w-px h-5 bg-app shrink-0" aria-hidden />
          <WorkspaceSwitcher empresa={empresa} empresas={empresas} />
        </div>

        <div className="flex-1" />

        {/* ═══ DIREITA: Agents · Admin · Platform · Sino · Avatar ═══ */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Agents */}
          {hasAgents && (
            <>
              <Link
                href={TOP_AGENTS.href}
                className={`
                  relative hidden sm:inline-flex items-center gap-2 text-sm font-medium
                  px-3 py-1.5 rounded-xl transition-all duration-150
                  ${isAgentsActive
                    ? ""
                    : "text-app-2 hover:bg-app-hover hover:text-app-1"}
                `}
                style={isAgentsActive ? { background: "var(--text-1)", color: "var(--surface)" } : undefined}
              >
                <NavIcon d={TOP_AGENTS.icon} className={`w-[15px] h-[15px] ${isAgentsActive ? "text-emerald-300" : "text-emerald-500"}`} />
                {t(TOP_AGENTS.labelKey)}
              </Link>
              <Link
                href={TOP_AGENTS.href}
                className={`sm:hidden p-1.5 rounded-lg hover:bg-app-hover ${isAgentsActive ? "bg-app-hover" : ""}`}
                aria-label={t(TOP_AGENTS.labelKey)}
              >
                <NavIcon d={TOP_AGENTS.icon} className="w-5 h-5 text-emerald-500" />
              </Link>
            </>
          )}

          {/* Admin */}
          {isAdmin && (
            <>
              <Link
                href={TOP_ADMIN.href}
                className={`
                  hidden sm:inline-flex items-center gap-2 text-sm font-medium
                  px-3 py-1.5 rounded-xl transition-all duration-150
                  ${isAdminActive ? "" : "text-app-2 hover:bg-app-hover hover:text-app-1"}
                `}
                style={isAdminActive ? { background: "var(--text-1)", color: "var(--surface)" } : undefined}
              >
                <NavIcon d={TOP_ADMIN.icon} className="w-[15px] h-[15px]" />
                {t(TOP_ADMIN.labelKey)}
              </Link>
              <Link
                href={TOP_ADMIN.href}
                className={`btn-icon btn-icon-sm sm:hidden ${isAdminActive ? "text-app-1 bg-app-hover" : ""}`}
                aria-label={t(TOP_ADMIN.labelKey)}
              >
                <NavIcon d={TOP_ADMIN.icon} className="w-5 h-5" />
              </Link>
            </>
          )}

          {/* Platform */}
          {isPlatformAdmin && (
            <>
              <Link
                href={TOP_PLATFORM.href}
                className={`
                  hidden sm:inline-flex items-center gap-2 text-sm font-medium
                  px-3 py-1.5 rounded-xl transition-all duration-150
                  ${isPlatformActive ? "" : "text-app-2 hover:bg-app-hover hover:text-app-1"}
                `}
                style={isPlatformActive ? { background: "var(--text-1)", color: "var(--surface)" } : undefined}
              >
                <NavIcon d={TOP_PLATFORM.icon} className={`w-[15px] h-[15px] ${isPlatformActive ? "text-rose-300" : "text-rose-500"}`} />
                {t(TOP_PLATFORM.labelKey)}
              </Link>
              <Link
                href={TOP_PLATFORM.href}
                className={`sm:hidden p-1.5 rounded-lg hover:bg-app-hover ${isPlatformActive ? "bg-app-hover" : ""}`}
                aria-label={t(TOP_PLATFORM.labelKey)}
              >
                <NavIcon d={TOP_PLATFORM.icon} className="w-5 h-5 text-rose-500" />
              </Link>
            </>
          )}

          {/* Divider antes do sino */}
          <span className="hidden sm:block w-px h-5 bg-app mx-1 shrink-0" aria-hidden />

          {/* Sino */}
          <NotificationBell userId={user.id} />

          {/* Divider antes do avatar */}
          <span className="hidden sm:block w-px h-5 bg-app mx-1 shrink-0" aria-hidden />

          {/* Avatar */}
          <div data-tour="topbar-perfil">
            <UserMenu user={user} empresa={empresa} empresas={empresas} />
          </div>
        </div>
      </div>
    </header>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TrialBanner — avisa o user sobre módulos em trial (com expiração)
// ═════════════════════════════════════════════════════════════════════════════

function TrialBanner({ modulos }: { modulos: Modulo[] }) {
  const { t } = useTranslation();
  const trials = modulos.filter(m => m.fonte === "override" && m.expira_em);
  if (trials.length === 0) return null;

  const now = Date.now();
  const items = trials.map(m => {
    const expiraDate = new Date(m.expira_em as string);
    const diasRest = Math.max(0, Math.ceil((expiraDate.getTime() - now) / 86_400_000));
    return { nome: m.nome, dias: diasRest, slug: m.slug };
  });

  const headline = items.length === 1
    ? t("trial.youHaveOne")
    : t("trial.youHaveMany", { count: items.length });

  return (
    <div className="mb-5 rounded-2xl border border-amber-200 dark:border-amber-800/50 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 px-5 py-3 flex items-center gap-3">
      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
      </svg>
      <div className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed flex-1">
        <strong>{headline}</strong>{" "}
        {items.map((it, i) => (
          <span key={it.slug}>
            <span className="font-semibold">{it.nome}</span> ({it.dias === 0
              ? t("trial.expiresToday")
              : t("trial.daysRemaining", { count: it.dias })})
            {i < items.length - 1 ? ", " : ""}
          </span>
        ))}
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
// Layout default export
// ═════════════════════════════════════════════════════════════════════════════

export default function Layout({ children }: { children: ReactNode }) {
  // R19_TYPES_APPLIED
  const { t } = useTranslation();
  const [me, setMe]   = useState<MeResponse | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [collapsed, setCollapsed]   = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const router = useRouter();
  const { syncFromServer } = useTheme();

  // Load /me — pode ser chamado de novo via refreshMe()
  const loadMe = async () => {
    try {
      const { data } = await api.get<MeResponse>("/me");
      setMe(data);
      setErro(null);
    } catch (e: any) {
      setErro(e?.response?.data?.detail || e?.message || "Erro ao carregar sua sessão");
    }
  };

  useEffect(() => {
    loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sincroniza preferences do servidor com ThemeProvider quando /me carrega
  useEffect(() => {
    if (me?.user?.preferences) syncFromServer(me.user.preferences);
  }, [me?.user?.preferences, syncFromServer]);

  // Persiste role no localStorage pra CommandPalette filtrar comandos por permissão
  useEffect(() => {
    if (!me?.user?.role) return;
    try { localStorage.setItem("miller.me.role.v1", me.user.role); } catch {}
  }, [me?.user?.role]);

  // Restore collapsed state
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = localStorage.getItem("sidebarCollapsed");
      if (saved === "1") setCollapsed(true);
    } catch {}
  }, []);

  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem("sidebarCollapsed", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  useEffect(() => { setDrawerOpen(false); }, [router.asPath]);

  // Erro state
  if (erro) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app px-4">
        <div className="bg-app-surface rounded-2xl shadow-sm border border-app max-w-md w-full p-8 text-center">
          <h1 className="font-serif text-2xl text-app-1 mb-2">{t("errorPage.loadProfileFailed")}</h1>
          <p className="text-sm text-app-3 mb-4">{erro}</p>
          <p className="text-xs text-app-muted">
            {t("errorPage.noCompanyHint")}
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (!me) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <div className="flex items-center gap-3 text-sm text-app-3">
          <div className="w-4 h-4 border-2 border-app-strong border-t-app-1 rounded-full animate-spin" style={{ borderColor: "var(--border)", borderTopColor: "var(--text-1)" }} />
          {t("common.loading")}
        </div>
      </div>
    );
  }

  // R16: força criar senha no primeiro acesso de user convidado.
  // /auth/primeira-senha é SEMI_PUBLIC (renderiza sem Layout), então quem chega aqui
  // com a flag ligada simplesmente é redirecionado.
  if (me.user.must_change_password) {
    if (typeof window !== "undefined" && router.asPath !== "/auth/primeira-senha") {
      router.replace("/auth/primeira-senha");
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <div className="flex items-center gap-3 text-sm text-app-3">
          <div className="w-4 h-4 border-2 border-app-strong border-t-app-1 rounded-full animate-spin" style={{ borderColor: "var(--border)", borderTopColor: "var(--text-1)" }} />
          {t("common.loading")}
        </div>
      </div>
    );
  }

  // R17: user com 2+ empresas precisa escolher qual entrar (uma vez por sessão).
  // /escolher-empresa é SEMI_PUBLIC. Sessão é marcada com sessionStorage flag.
  const empresas = me.empresas ?? [];
  if (empresas.length >= 2 && typeof window !== "undefined") {
    let jaEscolheu = false;
    try { jaEscolheu = sessionStorage.getItem("miller.escolheu_empresa") === "1"; } catch {}
    if (!jaEscolheu && router.asPath !== "/escolher-empresa") {
      router.replace("/escolher-empresa");
      return (
        <div className="min-h-screen flex items-center justify-center bg-app">
          <div className="flex items-center gap-3 text-sm text-app-3">
            <div className="w-4 h-4 border-2 border-app-strong border-t-app-1 rounded-full animate-spin" style={{ borderColor: "var(--border)", borderTopColor: "var(--text-1)" }} />
            {t("common.loading")}
          </div>
        </div>
      );
    }
  }

  // Filtra módulos da sidebar pelos que a empresa tem habilitados
  const enabledSlugs  = new Set(me.modulos.map(m => m.slug));
  const knownSlugs    = new Set(SIDEBAR_MODULES.map(m => m.slug));
  // R18: slugs que aparecem no TOPBAR (não devem cair na sidebar dinâmica)
  const TOPBAR_SLUGS  = new Set(["agents"]);
  // Helper: nome humanizado quando não há tradução (ex: "relatorio-fiscal" → "Relatorio Fiscal")
  const humanize = (slug: string) =>
    slug.split(/[-_]/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(" ");
  // R18: módulos dinâmicos = habilitados que não são hardcoded e não estão no topbar
  const dynamicModules: NavModule[] = me.modulos
    .filter(m => !knownSlugs.has(m.slug) && !TOPBAR_SLUGS.has(m.slug))
    .map(m => ({
      slug:     m.slug,
      labelKey: m.nome || humanize(m.slug),
      icon:     moduleIconPath(m.icone, "M4 6h16M4 12h16M4 18h16"),
      href:     `/${m.slug}`,
    } as NavModule));
  // R18: para módulos hardcoded, sobrescreve ícone com o do banco se admin definiu
  const moduloByslug = new Map(me.modulos.map(m => [m.slug, m]));
  const hardcodedVisible = SIDEBAR_MODULES
    .filter(m => enabledSlugs.has(m.slug))
    .map(m => {
      const dbModulo = moduloByslug.get(m.slug);
      return {
        ...m,
        icon: moduleIconPath(dbModulo?.icone, m.icon),
        labelKey: dbModulo?.nome ? `__direct:${dbModulo.nome}` : m.labelKey,
      };
    });
  const visibleModules = [...hardcodedVisible, ...dynamicModules];
  const isAdmin         = TOP_ADMIN.roles!.includes(me.user.role);
  const isPlatformAdmin = TOP_PLATFORM.roles!.includes(me.user.role);
  const hasAgents = enabledSlugs.has("agents");

  return (
    <MeContext.Provider value={{ me, refreshMe: loadMe }}>
      <div className="min-h-screen" style={{ background: "var(--bg)" }}>
        <DesktopSidebar
          modules={visibleModules}
          currentPath={router.asPath}
          collapsed={collapsed}
          onToggle={toggleCollapsed}
        />

        <MobileDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          modules={visibleModules}
          currentPath={router.asPath}
        />

        <div
          className={`flex flex-col min-h-screen transition-[padding-left] duration-200 ease-out ${
            collapsed ? "lg:pl-[4.5rem]" : "lg:pl-56"
          }`}
        >
          <TopBar
            user={me.user}
            empresa={me.empresa}
            empresas={empresas}
            isAdmin={isAdmin}
            isPlatformAdmin={isPlatformAdmin}
            hasAgents={hasAgents}
            currentPath={router.asPath}
            onMobileMenu={() => setDrawerOpen(true)}
          />
          <main className="flex-1 px-4 sm:px-6 lg:px-10 py-8 mx-auto w-full max-w-[1400px]">
            <TrialBanner modulos={me.modulos} />
            {children}
          </main>
          <Footer />
        </div>
        <OnboardingTour />
      </div>
    </MeContext.Provider>
  );
}
